import { Component, type ReactNode } from 'react'
import { AlertTriangle, RotateCcw } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback

      return (
        <div className="flex items-center justify-center min-h-[400px] p-8" role="alert">
          <div className="glass-elevated rounded-[var(--radius-md)] p-8 max-w-md w-full text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-error-muted flex items-center justify-center mx-auto">
              <AlertTriangle className="w-6 h-6 text-error" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary mb-1">Something went wrong</h2>
              <p className="text-sm text-text-muted">
                {this.state.error?.message || 'An unexpected error occurred.'}
              </p>
            </div>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-accent bg-accent-muted rounded-[var(--radius-sm)] hover:bg-accent/20 transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ring-offset"
            >
              <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
              Try Again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
